/**
 * Leitura do tipo de meta (Individual x Coletiva) ao reabrir uma meta salva.
 *
 * O tipo de meta NAO e persistido: nem o payload de gravacao
 * (ModalCadastroMeta.salvarMeta) nem a vw_campanha_meta tem coluna para ele.
 * A diferenca existe so no formato do dado gravado — as duas gravam uma linha
 * por (item x participante), mas:
 *
 *   coletiva   -> o mesmo valor_meta/valor_bonus/faixas replicado em todos os
 *                 participantes daquele item;
 *   individual -> cada participante com os seus proprios valores.
 *
 * Entao o tipo e INFERIDO: se as linhas de um mesmo item divergem entre
 * participantes, a meta so pode ter sido cadastrada como individual.
 *
 * Ambiguidade conhecida e inofensiva: uma meta individual em que todos os
 * participantes acabaram com valores iguais e indistinguivel de uma coletiva,
 * e reabre como coletiva. Como os valores sao identicos, o campo coletivo ja
 * vem com esse mesmo valor e a proxima gravacao o replica de volta — nenhum
 * dado se perde. O caso perigoso e o inverso (individual divergente reabrindo
 * como coletiva, achatando todo mundo no valor da primeira linha), e e
 * exatamente esse que a deteccao cobre.
 */

import { ordenarFaixas } from "./faixasLeituraUtils";

// Chave do item dentro da meta, por tipo de conteudo (1 Indicador,
// 2 Chassi, 3 Modelo, 4 Peca, 5 Servico).
const CHAVE_ITEM = {
  1: row => row.id_indicador,
  2: row => row.chassi_completo,
  3: row => row.id_produto,
  4: row => row.id_peca,
  5: row => row.id_servico
};

function chaveItem(row, tipoConteudo) {
  const fn = CHAVE_ITEM[tipoConteudo];
  return fn ? fn(row) : null;
}

// O participante e o VINCULO (colaborador, empresa) — a mesma pessoa em duas
// empresas da campanha sao duas linhas legitimas com valores iguais numa meta
// coletiva. Comparar por vinculo evita ler isso como divergencia.
function chaveVinculo(row) {
  return `${row.id_colaborador}|${row.id_empresa}`;
}

/**
 * Assinatura do que foi gravado para um participante num item. Meta fixa e um
 * par meta/bonus; faixa progressiva e a lista de faixas daquele participante,
 * que vem espalhada em varias linhas (uma por faixa) e por isso e acumulada.
 */
function assinaturaLinha(row, tipoCalculo) {
  if (tipoCalculo === 2) {
    return `${row.qtde_de}-${row.qtde_ate}-${row.valor_bonus}`;
  }
  return `${row.valor_meta}|${row.valor_bonus}`;
}

/**
 * Infere se a meta foi cadastrada como individual.
 *
 * @param {Array<Object>} linhas linhas da meta (mesmo id_campanha_meta)
 * @returns {boolean}
 */
export function detectarMetaIndividual(linhas) {
  const rows = (linhas || []).filter(r => r && r.id_colaborador != null);
  if (rows.length === 0) return false;

  const tipoConteudo = Number(rows[0].id_tipo_conteudo);
  const tipoCalculo = Number(rows[0].id_tipo_calculo);

  // item -> vinculo -> assinatura acumulada
  const porItem = new Map();
  rows.forEach(row => {
    const item = chaveItem(row, tipoConteudo);
    if (item == null) return;
    const chave = String(item);
    if (!porItem.has(chave)) porItem.set(chave, new Map());
    const porVinculo = porItem.get(chave);
    const vinculo = chaveVinculo(row);
    const parte = assinaturaLinha(row, tipoCalculo);
    // Faixa progressiva: varias linhas por participante. Ordena para nao
    // depender da ordem em que a API devolveu as faixas.
    porVinculo.set(vinculo, porVinculo.has(vinculo)
      ? [...porVinculo.get(vinculo).split("\n"), parte].sort().join("\n")
      : parte);
  });

  return [...porItem.values()].some(porVinculo => new Set(porVinculo.values()).size > 1);
}

/**
 * Monta form.metasIndividuaisItens (objetivos Modelo/Peca/Servico/Chassi):
 * { [id_colaborador]: { [chaveItem]: { metaValor, valorBonus } | { faixas } } }
 *
 * Chaveado so por id_colaborador porque e assim que o form e o payload leem
 * (ver calculoMixin.getIndividualItemValor e salvarMeta.getItemData) — a mesma
 * pessoa em duas empresas compartilha uma entrada, como ja acontece hoje.
 */
export function montarMetasIndividuaisItens(linhas) {
  const rows = (linhas || []).filter(r => r && r.id_colaborador != null);
  if (rows.length === 0) return {};

  const tipoConteudo = Number(rows[0].id_tipo_conteudo);
  const tipoCalculo = Number(rows[0].id_tipo_calculo);
  const itens = {};

  rows.forEach(row => {
    const item = chaveItem(row, tipoConteudo);
    if (item == null) return;
    if (!itens[row.id_colaborador]) itens[row.id_colaborador] = {};
    const doColaborador = itens[row.id_colaborador];

    if (tipoConteudo === 2) {
      doColaborador[item] = {
        valorBonus: row.valor_bonus != null ? String(row.valor_bonus) : ""
      };
      return;
    }

    if (tipoCalculo === 2) {
      if (row.qtde_de == null) return;
      if (!doColaborador[item]) doColaborador[item] = { faixas: [] };
      doColaborador[item].faixas.push({
        minimo: row.qtde_de,
        maximo: row.qtde_ate,
        bonus: row.valor_bonus
      });
      return;
    }

    doColaborador[item] = {
      metaValor: row.valor_meta != null ? String(row.valor_meta) : "",
      valorBonus: row.valor_bonus != null ? String(row.valor_bonus) : ""
    };
  });

  // A ordem das linhas vinda da API nao e a ordem das faixas (ver
  // faixasLeituraUtils): o form valida cada faixa contra a anterior no array.
  if (tipoConteudo !== 2 && tipoCalculo === 2) {
    Object.values(itens).forEach(doColaborador => {
      Object.values(doColaborador).forEach(item => {
        item.faixas = ordenarFaixas(item.faixas);
      });
    });
  }

  return itens;
}

/**
 * Monta form.metasIndividuais (objetivo Indicador):
 * { [id_colaborador]: { valorBonus, metas: { [id_indicador]: valor | { faixas } } } }
 *
 * Na meta fixa o bonus do indicador e um so por participante (o form tem um
 * unico campo de bonus por colaborador, ver SubEtapaCalculoIndicador) e
 * metas[id] guarda o valor escalar da meta; na faixa progressiva o bonus vive
 * dentro de cada faixa.
 */
export function montarMetasIndividuaisIndicador(linhas) {
  const rows = (linhas || []).filter(r => r && r.id_colaborador != null && r.id_indicador != null);
  if (rows.length === 0) return {};

  const tipoCalculo = Number(rows[0].id_tipo_calculo);
  const metasIndividuais = {};

  rows.forEach(row => {
    if (!metasIndividuais[row.id_colaborador]) {
      metasIndividuais[row.id_colaborador] = { valorBonus: null, metas: {} };
    }
    const dados = metasIndividuais[row.id_colaborador];

    if (tipoCalculo === 2) {
      if (row.qtde_de == null) return;
      if (!dados.metas[row.id_indicador]) dados.metas[row.id_indicador] = { faixas: [] };
      dados.metas[row.id_indicador].faixas.push({
        minimo: row.qtde_de,
        maximo: row.qtde_ate,
        bonus: row.valor_bonus
      });
      return;
    }

    dados.metas[row.id_indicador] = row.valor_meta != null ? String(row.valor_meta) : "";
    if (dados.valorBonus == null && row.valor_bonus != null) {
      dados.valorBonus = String(row.valor_bonus);
    }
  });

  if (tipoCalculo === 2) {
    Object.values(metasIndividuais).forEach(dados => {
      Object.values(dados.metas).forEach(meta => {
        meta.faixas = ordenarFaixas(meta.faixas);
      });
    });
  }

  return metasIndividuais;
}
